import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DateInput from "@/components/DateInput";
import { toast } from "sonner";
import { Factory, Package, CheckCircle, Clock, ArrowClockwise, Scales,
  ArrowRight, WarningCircle, XCircle, MagnifyingGlass, X, Printer, Receipt } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import SelectorLotesDialog from "../components/SelectorLotesDialog";
import AlertaExcesoKgTNModal from "../components/AlertaExcesoKgTNModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { useZebraPrinter, generarZplEtiqueta } from "../components/ZebraPrinter";
import api from "../lib/api";
import NumberInput from "@/components/ui/NumberInput";

const fmtKg = (n) => {
  const v = Number(n || 0);
  return `${v.toLocaleString("es-AR", { minimumFractionDigits: v % 1 === 0 ? 0 : 2, maximumFractionDigits: 3 })} kg`;
};

// Formatea la cantidad según la unidad del producto ("kg" o "unidades").
// Para unidades usa enteros; para kg mantiene decimales cuando corresponde.
const fmtQty = (n, unidad) => {
  const u = (unidad || "kg").toLowerCase();
  const v = Number(n || 0);
  if (u === "unidades" || u === "un" || u === "unidad") {
    const formatted = v.toLocaleString("es-AR", { maximumFractionDigits: 0 });
    return `${formatted} ${v === 1 ? "un" : "un"}`;
  }
  return fmtKg(v);
};

// Helper para manejar 409 del guard TN "tn_kg_excedidos" y mostrar cartel
// grande centrado. Reutilizado en ejecutarMarcarTerminado, marcarSinStock,
// guardarKg y set-kg de combos.
const handleErrorApi = (e, setExcesoKgAlerta, fallbackMsg = "No se pudo guardar") => {
  const detail = e?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.code === "tn_kg_excedidos") {
    setExcesoKgAlerta(detail);
    return true;
  }
  toast.error((typeof detail === "string" ? detail : detail?.message) || fallbackMsg);
  return false;
};

export default function OrdenesTrabajoPage() {
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState({ items: [], grupos: [], totales: { items: 0, terminados: 0, kg_esperados: 0, kg_producidos: 0 } });
  const [loading, setLoading] = useState(false);
  const [fechasConCarga, setFechasConCarga] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  // Cartel grande cuando un pedido TN intenta producir más kg que los pedidos.
  // Data viene del 409 del backend con code "tn_kg_excedidos".
  const [excesoKgAlerta, setExcesoKgAlerta] = useState(null);
  // Estado local de inputs de kg producidos (para no perder al re-render)
  const [inputKg, setInputKg] = useState({});
  // Filtro por cliente (multi-selección). Set vacío = mostrar todos.
  const [filterClientes, setFilterClientes] = useState(() => new Set());
  const [clienteOpen, setClienteOpen] = useState(false);
  // Filtro por producto (multi-selección). Set vacío = mostrar todos.
  const [filterProductos, setFilterProductos] = useState(() => new Set());
  const [productoOpen, setProductoOpen] = useState(false);
  // "En Facturación": pedidos que ya salieron de la OT (estado listo_para_facturar)
  const [enFacturacion, setEnFacturacion] = useState({ items: [], pedidos_meta: {}, total_pedidos: 0 });
  const [enFacDialog, setEnFacDialog] = useState(false);
  const [facturados, setFacturados] = useState({ items: [], pedidos_meta: {}, total_pedidos: 0 });
  const [facturadosDialog, setFacturadosDialog] = useState(false);
  // Dialog de terminados del día
  const [terminadosDialog, setTerminadosDialog] = useState(false);

  // Notificaciones persistentes de pedidos nuevos ingresados mientras se trabaja
  const [notifs, setNotifs] = useState([]);
  const knownPedidosRef = useRef(null); // Set<"source_type:source_id"> | null (aún sin inicializar)
  const anunciarRef = useRef(() => {}); // se completa más abajo
  // Dialog de impresión de etiquetas Zebra al terminar producto
  const [printItem, setPrintItem] = useState(null); // {it, defaultCopies}
  const zebraOutputConfigRef = useRef(null);
  useEffect(() => {
    api.get("/label-templates/zebra_output")
      .then(({ data }) => { zebraOutputConfigRef.current = data?.config || null; })
      .catch(() => { zebraOutputConfigRef.current = null; });
  }, []);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [{ data: d }, { data: ef }, { data: fc }] = await Promise.all([
        api.get("/produccion/ordenes-trabajo", { params: { fecha } }),
        api.get("/produccion/en-facturacion", { params: { fecha } }).catch(() => ({ data: { items: [], pedidos_meta: {}, total_pedidos: 0 } })),
        api.get("/produccion/facturados", { params: { fecha } }).catch(() => ({ data: { items: [], pedidos_meta: {}, total_pedidos: 0 } })),
      ]);

      setData(d);
      setEnFacturacion(ef);
      setFacturados(fc);
      // Semilla los inputs con kg_producidos actuales (o vacío para que el operador complete)
      const seed = {};
      const seedCombo = {};
      (d.items || []).forEach(it => {
        const k = `${it.source_type}:${it.source_id}:${it.item_idx}`;
        seed[k] = it.kg_producidos != null ? String(it.kg_producidos) : "";
        if (it.es_combo && Array.isArray(it.combo_componentes)) {
          it.combo_componentes.forEach((c, cidx) => {
            const kc = `${k}:${cidx}`;
            seedCombo[kc] = c.kg_producidos != null ? String(c.kg_producidos) : "";
          });
        }
      });
      setInputKg((cur) => ({ ...seed, ...cur })); // no sobreescribir lo que el operador esté editando
      setComboInputKg((cur) => ({ ...seedCombo, ...cur }));
    } catch (e) {
      if (!silent) toast.error("No se pudieron cargar las órdenes de trabajo");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [fecha]);

  useEffect(() => { load(); }, [load]);

  // Poll silencioso cada 20s del día visible (para que la vista se refresque
  // con kg_producidos u otros cambios hechos por otros usuarios)
  useEffect(() => {
    const iv = setInterval(() => load({ silent: true }), 20000);
    return () => clearInterval(iv);
  }, [load]);

  // Poll INDEPENDIENTE del día visible: detecta si ingresó un pedido nuevo
  // para HOY sin importar qué fecha esté mirando el operador. Dispara el
  // aviso por voz y la notificación arriba a la derecha.
  const checkPedidosHoy = useCallback(async () => {
    const hoy = new Date().toISOString().slice(0, 10);
    try {
      const { data: d } = await api.get("/produccion/ordenes-trabajo", { params: { fecha: hoy } });
      const currentPedidoKeys = new Map();
      (d.items || []).forEach((it) => {
        const k = `${it.source_type}:${it.source_id}`;
        if (!currentPedidoKeys.has(k)) {
          currentPedidoKeys.set(k, {
            numero: it.numero_pedido || "",
            cliente_nombre: it.cliente_nombre || "",
            source_type: it.source_type,
          });
        }
      });
      const known = knownPedidosRef.current;
      if (known === null) {
        knownPedidosRef.current = new Set(currentPedidoKeys.keys());
        return;
      }
      const nuevos = [];
      for (const [k, info] of currentPedidoKeys.entries()) {
        if (!known.has(k)) nuevos.push({ key: k, ...info });
      }
      if (nuevos.length > 0) {
        setNotifs((prev) => [
          ...nuevos.map((n) => ({
            id: `${n.key}-${Date.now()}`,
            numero: n.numero,
            cliente: n.cliente_nombre,
            origen: n.source_type,
            at: new Date(),
          })),
          ...prev,
        ]);
        try { anunciarRef.current(); } catch (_) {}
      }
      knownPedidosRef.current = new Set(currentPedidoKeys.keys());
    } catch (_) {}
  }, []);

  useEffect(() => {
    // Semilla y poll de pedidos de HOY (siempre, independiente de la fecha visible)
    checkPedidosHoy();
    const iv = setInterval(checkPedidosHoy, 20000);
    return () => clearInterval(iv);
  }, [checkPedidosHoy]);

  // Al cambiar de fecha visible NO reseteamos el tracker de HOY.
  // Sólo limpiamos las tarjetas de notificación en pantalla si el operador
  // se mueve de día, para no confundirlo con contexto viejo.
  useEffect(() => {
    setNotifs([]);
  }, [fecha]);

  const cerrarNotif = (id) => setNotifs((arr) => arr.filter((n) => n.id !== id));
  const cerrarTodasNotifs = () => setNotifs([]);

  // Voz de mujer al recibir un pedido nuevo. Se elige la primera voz ES femenina
  // disponible en el navegador (Windows/Chrome trae "Microsoft Sabina", "Elena",
  // "Helena", "Paulina", etc.). Se cachea al primer uso.
  const voiceRef = useRef(null);
  const pickFemaleVoice = useCallback(() => {
    if (!("speechSynthesis" in window)) return null;
    if (voiceRef.current) return voiceRef.current;
    const voices = window.speechSynthesis.getVoices() || [];
    const spanish = voices.filter(v => (v.lang || "").toLowerCase().startsWith("es"));
    // Priorizar voces con timbre cálido/sensual conocidas por SO:
    //  - Mónica / Paulina (macOS) → más aterciopeladas que Helena
    //  - Sabina (Windows es-MX)   → más suave que Elena
    //  - Google español female    → natural y grave
    const preferido = [
      /google\s+español|google\s+es-es|google\s+es-us|google\s+es-mx/i,
      /monica|mónica|paulina|sabina/i,
      /helena|elena|marisol|conchita|lucia|lucía|female|mujer/i,
    ];
    let pick = null;
    for (const re of preferido) {
      pick = spanish.find(v => re.test(v.name));
      if (pick) break;
    }
    pick = pick || spanish[0] || voices[0] || null;
    voiceRef.current = pick;
    return pick;
  }, []);
  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    // getVoices() puede estar vacío hasta que dispare "voiceschanged"
    pickFemaleVoice();
    const onChange = () => { voiceRef.current = null; pickFemaleVoice(); };
    window.speechSynthesis.addEventListener("voiceschanged", onChange);
    // "Desbloquear" TTS al primer click/tecla del usuario (política de autoplay).
    // Sin esto, la voz de mujer no habla hasta que el operador interactúa.
    let unlocked = false;
    const unlock = () => {
      if (unlocked) return;
      unlocked = true;
      try {
        const u = new SpeechSynthesisUtterance(" ");
        u.volume = 0;
        window.speechSynthesis.speak(u);
      } catch (_) {}
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("click", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.speechSynthesis.removeEventListener("voiceschanged", onChange);
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [pickFemaleVoice]);
  const anunciarPedidoNuevo = useCallback(() => {
    if (!("speechSynthesis" in window)) return;
    try {
      const u = new SpeechSynthesisUtterance("Hay un nuevo pedido");
      u.lang = "es-AR";
      // Voz sensual: hablar despacio, tono un poco grave, volumen pleno
      u.rate = 0.72;   // más lento
      u.pitch = 0.85;  // un poco más grave que el default (1.0)
      u.volume = 1.0;
      const v = pickFemaleVoice();
      if (v) u.voice = v;
      window.speechSynthesis.speak(u);
    } catch (_) {}
  }, [pickFemaleVoice]);
  // Actualizar ref para que `load` (declarado antes) pueda invocarla
  useEffect(() => { anunciarRef.current = anunciarPedidoNuevo; }, [anunciarPedidoNuevo]);
  useEffect(() => {
    api.get("/produccion/fechas-envio").then(({ data }) => setFechasConCarga(data.fechas || [])).catch(() => {});
  }, []);

  // Lista de clientes únicos que hay en la fecha actual, para poblar el dropdown de filtro.
  const clientesEnDia = useMemo(() => {
    const set = new Set();
    (data.items || []).forEach((it) => {
      if (it.cliente_nombre) set.add(it.cliente_nombre);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [data.items]);

  // Lista de productos únicos que hay en la fecha actual, para el dropdown de filtro por producto.
  const productosEnDia = useMemo(() => {
    const set = new Set();
    (data.items || []).forEach((it) => {
      if (it.producto) set.add(it.producto);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [data.items]);

  // Agrupamiento visual: Rubro > Producto > Items
  // Todas las variantes/especificaciones de un mismo producto (por ej. "SUPREMA por kg"
  // con "CHURRASCOS FINOS, INTERFOLEADO" o "CHURRASCOS, INTERFOLEADO") caen en el mismo
  // grupo. La especificación se muestra como badge en cada fila.
  const gruposPorRubro = useMemo(() => {
    const rubros = new Map();
    // Aplicamos filtros por cliente(s) y/o producto(s) si hay alguno seleccionado
    const itemsFiltrados = (data.items || []).filter((it) => {
      if (filterClientes.size > 0 && !filterClientes.has(it.cliente_nombre)) return false;
      if (filterProductos.size > 0 && !filterProductos.has(it.producto)) return false;
      return true;
    });
    itemsFiltrados.forEach(it => {
      const rNombre = it.rubro_nombre || "Sin rubro";
      let grupoRubro = rubros.get(rNombre);
      if (!grupoRubro) {
        grupoRubro = {
          nombre: rNombre,
          color: it.rubro_color || "#921A0E",
          orden: it.rubro_orden ?? 9999,
          productos: new Map(),
        };
        rubros.set(rNombre, grupoRubro);
      }
      const clave = it.producto || "—";
      let entry = grupoRubro.productos.get(clave);
      if (!entry) {
        entry = { producto: it.producto, items: [] };
        grupoRubro.productos.set(clave, entry);
      }
      entry.items.push(it);
    });
    return Array.from(rubros.values()).sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre));
  }, [data.items, filterClientes, filterProductos]);

  // Mismo agrupamiento (rubro > producto > items) para "En Facturación".
  const gruposEnFacturacion = useMemo(() => {
    const rubros = new Map();
    (enFacturacion.items || []).forEach((it) => {
      const rNombre = it.rubro_nombre || "Sin rubro";
      let g = rubros.get(rNombre);
      if (!g) {
        g = { nombre: rNombre, color: it.rubro_color || "#921A0E", orden: it.rubro_orden ?? 9999, productos: new Map() };
        rubros.set(rNombre, g);
      }
      const clave = it.producto || "—";
      let entry = g.productos.get(clave);
      if (!entry) { entry = { producto: it.producto, items: [] }; g.productos.set(clave, entry); }
      entry.items.push(it);
    });
    return Array.from(rubros.values()).sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre));
  }, [enFacturacion.items]);

  // Terminados = items terminados de OT + items en-facturacion (con o sin factura). Dedupe por key.
  const gruposTerminados = useMemo(() => {
    const seen = new Set();
    const combined = [];
    for (const it of ((data.items || []).filter(x => x.terminado))) {
      const k = `${it.source_type}:${it.source_id}:${it.item_idx}`;
      if (seen.has(k)) continue;
      seen.add(k); combined.push(it);
    }
    for (const it of (enFacturacion.items || [])) {
      const k = `${it.source_type}:${it.source_id}:${it.item_idx}`;
      if (seen.has(k)) continue;
      seen.add(k); combined.push(it);
    }
    const rubros = new Map();
    combined.forEach((it) => {
      const rNombre = it.rubro_nombre || "Sin rubro";
      let g = rubros.get(rNombre);
      if (!g) {
        g = { nombre: rNombre, color: it.rubro_color || "#921A0E", orden: it.rubro_orden ?? 9999, productos: new Map() };
        rubros.set(rNombre, g);
      }
      const clave = it.producto || "—";
      let entry = g.productos.get(clave);
      if (!entry) { entry = { producto: it.producto, items: [] }; g.productos.set(clave, entry); }
      entry.items.push(it);
    });
    return Array.from(rubros.values()).sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre));
  }, [data.items, enFacturacion.items]);

  // Grupos de items facturados (rubro > producto > pedidos), para el dialog "Facturados"
  const gruposFacturados = useMemo(() => {
    const rubros = new Map();
    (facturados.items || []).forEach((it) => {
      const rNombre = it.rubro_nombre || "Sin rubro";
      let g = rubros.get(rNombre);
      if (!g) {
        g = { nombre: rNombre, color: it.rubro_color || "#921A0E", orden: it.rubro_orden ?? 9999, productos: new Map() };
        rubros.set(rNombre, g);
      }
      const clave = it.producto || "—";
      let entry = g.productos.get(clave);
      if (!entry) { entry = { producto: it.producto, items: [] }; g.productos.set(clave, entry); }
      entry.items.push(it);
    });
    return Array.from(rubros.values()).sort((a, b) => a.orden - b.orden || a.nombre.localeCompare(b.nombre));
  }, [facturados.items]);

  const [pendingLotes, setPendingLotes] = useState(null); // { it, kgP } cuando esperamos elegir lotes
  // Combos completados (unidades) por item. Sólo aplica a items es_combo.
  const [comboUnits, setComboUnits] = useState({});

  // Guarda combos completados (unidades) sin tocar kg/stock. Habilitado incluso
  // si el ítem está terminado — es un dato informativo editable post-cierre.
  const [savingCombos, setSavingCombos] = useState({});
  const guardarCombosCompletados = async (it, rawVal) => {
    const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
    const val = rawVal === "" ? null : Number(rawVal);
    if (val !== null && (!Number.isFinite(val) || val < 0)) return;
    if (val === (it.combos_completados ?? null)) return; // sin cambios
    setSavingCombos((s) => ({ ...s, [key]: true }));
    try {
      await api.post("/produccion/ordenes-trabajo/combo/set-combos-completados", {
        source_type: it.source_type,
        source_id: it.source_id,
        item_idx: it.item_idx,
        fecha_envio: data.fecha_envio,
        producto: it.producto,
        combos_completados: val,
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar los combos completados");
    } finally { setSavingCombos((s) => ({ ...s, [key]: false })); }
  };

  const marcarTerminado = async (it, terminado) => {
    const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
    // Para combos, kg producidos = SUMA de kg cargados por componente. El input
    // principal está oculto: el operario sólo carga por componente.
    let kgP;
    let combosDone = null;
    if (it.es_combo && Array.isArray(it.combo_componentes) && terminado) {
      kgP = (it.combo_componentes || []).reduce((s, c, cidx) => {
        const kc = `${key}:${cidx}`;
        const raw = comboInputKg[kc] ?? (c.kg_producidos != null ? String(c.kg_producidos) : "");
        const n = Number(raw);
        return s + (Number.isFinite(n) ? n : 0);
      }, 0);
      if (kgP <= 0) {
        toast.error("Cargá los kg de al menos un componente del combo antes de terminar");
        return;
      }
      kgP = Math.round(kgP * 1000) / 1000;
      // Combos completados: si el operario cargó el input de unidades lo usamos,
      // sino asumimos que completó todos los combos pedidos.
      const rawU = comboUnits[key];
      combosDone = rawU !== undefined && rawU !== "" ? Number(rawU) : (it.combos_esperados ?? null);
    } else {
      kgP = terminado
        ? (inputKg[key] === "" ? it.kg_esperados : Number(inputKg[key]))
        : (inputKg[key] === "" ? null : Number(inputKg[key]));
    }
    // Para combos, el límite de kg del cliente es la SUMA de los kg esperados
    // de cada componente (lo que realmente compró), no el rendimiento del ítem.
    const kgLimite = (it.es_combo && Array.isArray(it.combo_componentes) && it.combo_componentes.length > 0)
      ? Math.round(it.combo_componentes.reduce((s, c) => s + Number(c.kg_esperados || 0), 0) * 1000) / 1000
      : Number(it.kg_esperados || 0);
    // Pre-check TN: si va a superar los kg esperados, mostramos el cartel
    // grande AHORA y NO abrimos el selector de lotes. Evita que el operador
    // avance al selector cuando el backend ya lo va a rechazar con 409.
    if (terminado && it.source_type === "tn" && kgP != null) {
      if (kgLimite > 0 && Number(kgP) - kgLimite > 0.001) {
        setExcesoKgAlerta({
          code: "tn_kg_excedidos",
          kg_pedidos: kgLimite,
          kg_intentados: Number(kgP),
          excedente: Math.round((Number(kgP) - kgLimite) * 1000) / 1000,
          producto: it.producto,
          cliente: it.cliente_nombre || "",
          pedido_numero: it.tn_number || it.numero_pedido || null,
        });
        return;
      }
    }
    // Al marcar TERMINADO por primera vez → primero elegir lotes consumidos
    if (terminado && !it.terminado) {
      setPendingLotes({ it, kgP, combosDone });
      return;
    }
    await ejecutarMarcarTerminado(it, terminado, kgP, [], combosDone);
  };

  const ejecutarMarcarTerminado = async (it, terminado, kgP, lotes, combosDone = null) => {
    const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
    setSavingKey(key);
    try {
      await api.post("/produccion/ordenes-trabajo/marcar", {
        source_type: it.source_type,
        source_id: it.source_id,
        item_idx: it.item_idx,
        fecha_envio: data.fecha_envio,
        producto: it.producto,
        especificacion: it.especificacion || null,
        // Para combos, el límite que compara el backend es la SUMA de los kg
        // esperados de cada componente (lo que compró el cliente), no el
        // rendimiento del ítem.
        kg_esperados: (it.es_combo && Array.isArray(it.combo_componentes) && it.combo_componentes.length > 0)
          ? Math.round(it.combo_componentes.reduce((s, c) => s + Number(c.kg_esperados || 0), 0) * 1000) / 1000
          : it.kg_esperados,
        kg_producidos: kgP,
        terminado: !!terminado,
        sin_stock: false,
        lotes_consumidos: lotes || [],
        combos_completados: combosDone,
      });
      toast.success(terminado ? "Marcado como TERMINADO" : "Producción revertida");
      // Al terminar un producto, ofrecer imprimir etiquetas Zebra automáticamente.
      if (terminado) {
        const sameGroup = (data.items || []).filter((x) =>
          x.producto === it.producto && (x.especificacion || "") === (it.especificacion || "")
        );
        setPrintItem({
          it: { ...it, kg_producidos: kgP },
          defaultCopies: Math.max(1, sameGroup.length),
        });
      }
      load();
    } catch (e) {
      handleErrorApi(e, setExcesoKgAlerta);
    } finally {
      setSavingKey(null);
    }
  };

  const marcarSinStock = async (it, sinStock) => {
    const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
    setSavingKey(key);
    try {
      await api.post("/produccion/ordenes-trabajo/marcar", {
        source_type: it.source_type,
        source_id: it.source_id,
        item_idx: it.item_idx,
        fecha_envio: data.fecha_envio,
        producto: it.producto,
        especificacion: it.especificacion || null,
        kg_esperados: it.kg_esperados,
        kg_producidos: 0,
        terminado: false,
        sin_stock: !!sinStock,
      });
      toast.success(sinStock ? "Marcado SIN STOCK" : "Estado revertido");
      load();
    } catch (e) {
      handleErrorApi(e, setExcesoKgAlerta);
    } finally {
      setSavingKey(null);
    }
  };

  const guardarKg = async (it) => {
    const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
    if (inputKg[key] === "" || inputKg[key] == null) return;
    setSavingKey(key);
    try {
      await api.post("/produccion/ordenes-trabajo/marcar", {
        source_type: it.source_type,
        source_id: it.source_id,
        item_idx: it.item_idx,
        fecha_envio: data.fecha_envio,
        producto: it.producto,
        especificacion: it.especificacion || null,
        kg_esperados: it.kg_esperados,
        kg_producidos: Number(inputKg[key]),
        terminado: !!it.terminado,
        sin_stock: false, // confirmar un peso implica que hay stock
      });
    } catch (e) {
      handleErrorApi(e, setExcesoKgAlerta, "No se pudo guardar los kilos");
    } finally {
      setSavingKey(null);
    }
  };

  // Estado + persistencia de kg producidos por componente (para items combo)
  const [comboInputKg, setComboInputKg] = useState({});
  const guardarKgCombo = async (it, cidx, kg, producto) => {
    const key = `${it.source_type}:${it.source_id}:${it.item_idx}:${cidx}`;
    setSavingKey(key);
    try {
      // Buscamos el componente en `it.combo_componentes[cidx]` para propagar
      // `kg_esperados` y `ref_id` — así el indicador TN puede mapear precios
      // sin reconsultar la receta.
      const c = (it.combo_componentes || [])[cidx] || {};
      await api.post("/produccion/ordenes-trabajo/combo/set-kg", {
        source_type: it.source_type,
        source_id: it.source_id,
        item_idx: it.item_idx,
        fecha_envio: data.fecha_envio,
        componente_idx: cidx,
        kg_producidos: kg === "" || kg == null ? null : Number(kg),
        producto,
        kg_esperados: c.kg_esperados != null ? Number(c.kg_esperados) : null,
        ref_id: c.ref_id || null,
      });
      setComboInputKg((cur) => ({ ...cur, [key]: kg }));
    } catch (e) {
      handleErrorApi(e, setExcesoKgAlerta, "No se pudo guardar kg del componente");
    } finally {
      setSavingKey(null);
    }
  };

  const totales = data.totales || { items: 0, terminados: 0, kg_esperados: 0, kg_producidos: 0 };
  // Total de terminados = terminados en OT + los que ya pasaron a En Facturación (con o sin factura)
  const totalTerminadosCombinado = useMemo(() => {
    const seen = new Set();
    let count = 0;
    for (const it of ((data.items || []).filter(x => x.terminado))) {
      const k = `${it.source_type}:${it.source_id}:${it.item_idx}`;
      if (seen.has(k)) continue;
      seen.add(k); count++;
    }
    for (const it of (enFacturacion.items || [])) {
      const k = `${it.source_type}:${it.source_id}:${it.item_idx}`;
      if (seen.has(k)) continue;
      seen.add(k); count++;
    }
    return count;
  }, [data.items, enFacturacion.items]);
  const pct = totales.items ? Math.round((totales.terminados / totales.items) * 100) : 0;

  return (
    <div className="space-y-4 p-4">
      {/* Stack de notificaciones persistentes (pedidos nuevos entrantes) */}
      {notifs.length > 0 && (
        <div className="fixed top-16 right-4 z-50 flex flex-col gap-2 max-w-[360px]" data-testid="ot-notifs">
          {notifs.length > 1 && (
            <button
              onClick={cerrarTodasNotifs}
              className="self-end text-[10px] font-mono uppercase text-neutral-500 hover:text-red-700 tracking-wider"
              data-testid="ot-notifs-cerrar-todas"
            >
              cerrar todas ({notifs.length})
            </button>
          )}
          {notifs.map((n) => (
            <div
              key={n.id}
              className="bg-white border-l-4 border-emerald-500 rounded-md shadow-lg p-3 flex items-start gap-2 animate-in slide-in-from-right"
              data-testid={`ot-notif-${n.numero}`}
            >
              <div className="mt-0.5 shrink-0 w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                <Package size={18} weight="duotone" className="text-emerald-700" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase text-emerald-700 tracking-wider">
                  Pedido nuevo · {n.origen === "tn" ? "Tienda Nube" : "Manual"}
                </div>
                <div className="text-[13px] font-bold text-neutral-900 leading-tight">
                  {n.numero}
                </div>
                <div className="text-[12px] text-neutral-700 truncate" title={n.cliente}>
                  {n.cliente || "Sin cliente"}
                </div>
                <div className="text-[10px] text-neutral-400 font-mono mt-0.5">
                  {n.at.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })} hs
                </div>
              </div>
              <button
                onClick={() => cerrarNotif(n.id)}
                className="shrink-0 text-neutral-400 hover:text-neutral-800 -mt-1 -mr-1 p-0.5"
                data-testid={`ot-notif-cerrar-${n.numero}`}
                title="Cerrar"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="space-y-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <h1 className="text-2xl font-bold text-neutral-800 flex items-center gap-2">
            <Factory size={28} weight="fill" className="text-red-800" />
            Orden de Trabajo
          </h1>
          <Button variant="outline" onClick={load} className="h-9 gap-2" data-testid="ot-refresh">
            <ArrowClockwise size={15} weight="bold" /> Actualizar
          </Button>
        </div>

        {/* Barra de filtros — Fecha (HOY/MAÑANA/PERSONALIZADO) + Cliente + Producto */}
        <div className="flex flex-wrap items-end gap-3">
          {/* Fecha: HOY / MAÑANA / PERSONALIZADO */}
          <div>
            <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest mb-1">Fecha de envío</div>
            <div className="flex items-center gap-1 flex-wrap">
              {(() => {
                const hoy = new Date().toISOString().slice(0, 10);
                const manana = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                const isHoy = fecha === hoy;
                const isManana = fecha === manana;
                const isPers = !isHoy && !isManana;
                const btnBase = "h-9 rounded-sm text-xs font-mono uppercase tracking-widest px-3 border";
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => setFecha(hoy)}
                      className={`${btnBase} ${isHoy ? "bg-[#921A0E] text-white border-[#921A0E]" : "bg-white border-neutral-300 hover:bg-neutral-100"}`}
                      data-testid="ot-fecha-hoy"
                    >
                      Hoy
                    </button>
                    <button
                      type="button"
                      onClick={() => setFecha(manana)}
                      className={`${btnBase} ${isManana ? "bg-[#921A0E] text-white border-[#921A0E]" : "bg-white border-neutral-300 hover:bg-neutral-100"}`}
                      data-testid="ot-fecha-manana"
                    >
                      Mañana
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        // Al pasar a personalizado, si aún estaba en hoy/mañana ponemos +2 días para señalar el modo
                        if (!isPers) {
                          const dos = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
                          setFecha(dos);
                        }
                      }}
                      className={`${btnBase} ${isPers ? "bg-[#921A0E] text-white border-[#921A0E]" : "bg-white border-neutral-300 hover:bg-neutral-100"}`}
                      data-testid="ot-fecha-personalizado"
                    >
                      Personalizado
                    </button>
                    {isPers && (
                      <DateInput
                        value={fecha}
                        onChange={(e) => setFecha(e.target.value)}
                        className="w-44 font-mono h-9"
                        list="fechas-con-carga"
                        data-testid="ot-fecha"
                      />
                    )}
                    <datalist id="fechas-con-carga">
                      {fechasConCarga.map((f) => <option key={f} value={f}>{f}</option>)}
                    </datalist>
                  </>
                );
              })()}
            </div>
          </div>

          {/* Filtro por cliente (multi-selección) */}
          <div>
            <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest mb-1">Filtro por cliente</div>
            <div className="flex items-center gap-1">
              <Popover open={clienteOpen} onOpenChange={setClienteOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-9 gap-2 min-w-[220px] justify-between ${filterClientes.size > 0 ? "border-amber-400 bg-amber-50/50" : ""}`}
                    data-testid="ot-filter-cliente-trigger"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <MagnifyingGlass size={14} className="text-neutral-500 shrink-0" />
                      <span className="text-xs truncate">
                        {filterClientes.size === 0 ? (
                          <span className="text-neutral-500">Filtrar por cliente…</span>
                        ) : filterClientes.size === 1 ? (
                          Array.from(filterClientes)[0]
                        ) : (
                          <span className="font-medium">{filterClientes.size} clientes seleccionados</span>
                        )}
                      </span>
                    </div>
                    {filterClientes.size > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setFilterClientes(new Set()); }}
                        className="p-0.5 rounded hover:bg-neutral-200 shrink-0"
                        data-testid="ot-filter-cliente-clear"
                        aria-label="Limpiar filtro"
                      >
                        <X size={13} />
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[320px]" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar cliente…" autoFocus data-testid="ot-filter-cliente-input" />
                    <CommandList>
                      <CommandEmpty>Sin clientes que coincidan.</CommandEmpty>
                      <CommandGroup heading={`${clientesEnDia.length} cliente${clientesEnDia.length !== 1 ? "s" : ""} con pedidos el ${fecha}`}>
                        <CommandItem
                          value="__todos__"
                          onSelect={() => { setFilterClientes(new Set()); }}
                          data-testid="ot-filter-cliente-todos"
                        >
                          <span className="text-neutral-500 italic">— Todos los clientes —</span>
                          {filterClientes.size === 0 && (
                            <CheckCircle size={14} weight="fill" className="ml-auto text-amber-600" />
                          )}
                        </CommandItem>
                        {clientesEnDia.map((nombre) => {
                          const count = (data.items || []).filter((it) => it.cliente_nombre === nombre).length;
                          const selected = filterClientes.has(nombre);
                          return (
                            <CommandItem
                              key={nombre}
                              value={nombre}
                              onSelect={() => {
                                setFilterClientes((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(nombre)) next.delete(nombre);
                                  else next.add(nombre);
                                  return next;
                                });
                              }}
                              data-testid={`ot-filter-cliente-item-${nombre}`}
                            >
                              <span className={`inline-flex items-center justify-center w-4 h-4 rounded border mr-2 shrink-0 ${selected ? "bg-amber-500 border-amber-500 text-white" : "border-neutral-300 bg-white"}`}>
                                {selected && <CheckCircle size={12} weight="fill" />}
                              </span>
                              <span className="flex-1 truncate">{nombre}</span>
                              <Badge variant="outline" className="text-[10px] bg-neutral-50">
                                {count} ítem{count !== 1 ? "s" : ""}
                              </Badge>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {filterClientes.size > 0 && (
                <span className="text-[10px] font-mono uppercase text-amber-700 tracking-widest hidden lg:inline">
                  {filterClientes.size === 1 ? "Filtrado" : `${filterClientes.size} filtros`}
                </span>
              )}
            </div>
          </div>

          {/* Filtro por producto (multi-selección) */}
          <div>
            <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest mb-1">Filtro por producto</div>
            <div className="flex items-center gap-1">
              <Popover open={productoOpen} onOpenChange={setProductoOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={`h-9 gap-2 min-w-[220px] justify-between ${filterProductos.size > 0 ? "border-amber-400 bg-amber-50/50" : ""}`}
                    data-testid="ot-filter-producto-trigger"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <MagnifyingGlass size={14} className="text-neutral-500 shrink-0" />
                      <span className="text-xs truncate">
                        {filterProductos.size === 0 ? (
                          <span className="text-neutral-500">Filtrar por producto…</span>
                        ) : filterProductos.size === 1 ? (
                          Array.from(filterProductos)[0]
                        ) : (
                          <span className="font-medium">{filterProductos.size} productos seleccionados</span>
                        )}
                      </span>
                    </div>
                    {filterProductos.size > 0 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setFilterProductos(new Set()); }}
                        className="p-0.5 rounded hover:bg-neutral-200 shrink-0"
                        data-testid="ot-filter-producto-clear"
                        aria-label="Limpiar filtro"
                      >
                        <X size={13} />
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[360px]" align="start">
                  <Command>
                    <CommandInput placeholder="Buscar producto…" autoFocus data-testid="ot-filter-producto-input" />
                    <CommandList>
                      <CommandEmpty>Sin productos que coincidan.</CommandEmpty>
                      <CommandGroup heading={`${productosEnDia.length} producto${productosEnDia.length !== 1 ? "s" : ""} con pedidos el ${fecha}`}>
                        <CommandItem
                          value="__todos__"
                          onSelect={() => { setFilterProductos(new Set()); }}
                          data-testid="ot-filter-producto-todos"
                        >
                          <span className="text-neutral-500 italic">— Todos los productos —</span>
                          {filterProductos.size === 0 && (
                            <CheckCircle size={14} weight="fill" className="ml-auto text-amber-600" />
                          )}
                        </CommandItem>
                        {productosEnDia.map((nombre) => {
                          const count = (data.items || []).filter((it) => it.producto === nombre).length;
                          const selected = filterProductos.has(nombre);
                          return (
                            <CommandItem
                              key={nombre}
                              value={nombre}
                              onSelect={() => {
                                setFilterProductos((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(nombre)) next.delete(nombre);
                                  else next.add(nombre);
                                  return next;
                                });
                              }}
                              data-testid={`ot-filter-producto-item-${nombre}`}
                            >
                              <span className={`inline-flex items-center justify-center w-4 h-4 rounded border mr-2 shrink-0 ${selected ? "bg-amber-500 border-amber-500 text-white" : "border-neutral-300 bg-white"}`}>
                                {selected && <CheckCircle size={12} weight="fill" />}
                              </span>
                              <span className="flex-1 truncate">{nombre}</span>
                              <Badge variant="outline" className="text-[10px] bg-neutral-50">
                                {count} ítem{count !== 1 ? "s" : ""}
                              </Badge>
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {filterProductos.size > 0 && (
                <span className="text-[10px] font-mono uppercase text-amber-700 tracking-widest hidden lg:inline">
                  {filterProductos.size === 1 ? "Filtrado" : `${filterProductos.size} filtros`}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats + progress */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="En producción" value={`${totales.terminados} / ${totales.items}`} icon={CheckCircle} colorClass="text-emerald-600" />
        <button
          type="button"
          onClick={() => totalTerminadosCombinado > 0 && setTerminadosDialog(true)}
          disabled={totalTerminadosCombinado === 0}
          className={`text-left rounded border p-3 transition ${
            totalTerminadosCombinado > 0
              ? "border-emerald-300 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 cursor-pointer"
              : "border-neutral-200 bg-neutral-50 cursor-default opacity-70"
          }`}
          data-testid="ot-btn-terminados"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase text-emerald-700 font-mono tracking-widest">Terminados</div>
              <div className={`text-2xl font-bold mt-1 ${totalTerminadosCombinado > 0 ? "text-emerald-800" : "text-neutral-400"}`}>
                {totalTerminadosCombinado}
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                {totalTerminadosCombinado === 1 ? "pedido terminado" : "pedidos terminados"}
              </div>
            </div>
            <CheckCircle size={18} weight="fill" className={totalTerminadosCombinado > 0 ? "text-emerald-600" : "text-neutral-300"} />
          </div>
        </button>
        <button
          type="button"
          onClick={() => enFacturacion.total_pedidos > 0 && setEnFacDialog(true)}
          disabled={enFacturacion.total_pedidos === 0}
          className={`text-left rounded border p-3 transition ${
            enFacturacion.total_pedidos > 0
              ? "border-amber-300 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 cursor-pointer"
              : "border-neutral-200 bg-neutral-50 cursor-default opacity-70"
          }`}
          data-testid="ot-btn-en-facturacion"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase text-amber-700 font-mono tracking-widest">En Facturación</div>
              <div className={`text-2xl font-bold mt-1 ${enFacturacion.total_pedidos > 0 ? "text-amber-800" : "text-neutral-400"}`}>
                {enFacturacion.total_pedidos}
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                {enFacturacion.total_pedidos === 1 ? "pedido listo" : "pedidos listos"}
              </div>
            </div>
            <ArrowRight size={18} className={enFacturacion.total_pedidos > 0 ? "text-amber-600" : "text-neutral-300"} />
          </div>
        </button>
        <button
          type="button"
          onClick={() => facturados.total_pedidos > 0 && setFacturadosDialog(true)}
          disabled={facturados.total_pedidos === 0}
          className={`text-left rounded border p-3 transition ${
            facturados.total_pedidos > 0
              ? "border-blue-300 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 cursor-pointer"
              : "border-neutral-200 bg-neutral-50 cursor-default opacity-70"
          }`}
          data-testid="ot-btn-facturados"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase text-blue-700 font-mono tracking-widest">Facturados</div>
              <div className={`text-2xl font-bold mt-1 ${facturados.total_pedidos > 0 ? "text-blue-800" : "text-neutral-400"}`}>
                {facturados.total_pedidos}
              </div>
              <div className="text-[10px] text-neutral-500 mt-0.5">
                {facturados.total_pedidos === 1 ? "pedido facturado" : "pedidos facturados"}
              </div>
            </div>
            <Receipt size={18} weight="duotone" className={facturados.total_pedidos > 0 ? "text-blue-600" : "text-neutral-300"} />
          </div>
        </button>
      </div>

      {/* Grupos de producto */}
      {loading ? (
        <div className="text-center py-16 text-neutral-500">Cargando...</div>
      ) : gruposPorRubro.length === 0 ? (
        <div className="border-2 border-dashed border-neutral-300 rounded p-16 text-center">
          <WarningCircle size={40} className="mx-auto text-neutral-300 mb-3" />
          <div className="text-neutral-500">Sin pedidos con envío el <strong>{fecha}</strong>.</div>
          <div className="text-xs text-neutral-400 mt-1">
            Cargá la <strong>fecha de envío</strong> desde <em>Tienda Nube</em> o <em>Pedidos</em> para que aparezcan acá.
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {gruposPorRubro.map((rubro) => {
            const productosArr = Array.from(rubro.productos.values())
              // Grupos con todos los items resueltos (terminados o sin stock) van al final
              .slice()
              .sort((a, b) => {
                const aDone = a.items.every(x => x.terminado || x.sin_stock) ? 1 : 0;
                const bDone = b.items.every(x => x.terminado || x.sin_stock) ? 1 : 0;
                return aDone - bDone;
              });
            const rubroItems = productosArr.flatMap((g) => g.items);
            const totalKgRubro = rubroItems.reduce((s, x) => s + x.kg_esperados, 0);
            const prodKgRubro = rubroItems.reduce((s, x) => s + Number(x.kg_producidos || 0), 0);
            const doneRubro = rubroItems.filter(x => x.terminado).length;
            const allDoneRubro = doneRubro === rubroItems.length;
            return (
              <div key={rubro.nombre} className="space-y-2" data-testid={`ot-rubro-${rubro.nombre}`}>
                {/* Header de RUBRO */}
                <div
                  className="rounded flex items-center gap-3 px-4 py-3 text-white font-bold shadow-sm"
                  style={{ background: rubro.color }}
                >
                  <div
                    className="w-3 h-3 rounded-full bg-white/80"
                    style={{ boxShadow: `0 0 0 3px ${rubro.color}` }}
                  />
                  <div className="text-2xl uppercase tracking-wider flex-1">{rubro.nombre}</div>
                  <Badge variant="outline" className="bg-white/20 text-white border-white/30 text-sm font-mono px-3 py-1">
                    {productosArr.length} grupo{productosArr.length !== 1 ? "s" : ""} · {fmtKg(totalKgRubro)}
                  </Badge>
                  <Badge variant="outline" className={`text-sm font-mono px-3 py-1 ${allDoneRubro ? "bg-emerald-500 text-white border-emerald-300" : "bg-white/20 text-white border-white/30"}`}>
                    {doneRubro} / {rubroItems.length} listos
                  </Badge>
                </div>

                {/* Grupos por PRODUCTO dentro del RUBRO (todas las variantes/especificaciones juntas) */}
                {productosArr.map((grupo) => {
                  const { producto } = grupo;
                  // Dentro del grupo: pendientes primero, terminados / sin stock al final
                  const items = (grupo.items || []).slice().sort((a, b) => {
                    const aDone = (a.terminado || a.sin_stock) ? 1 : 0;
                    const bDone = (b.terminado || b.sin_stock) ? 1 : 0;
                    return aDone - bDone;
                  });
                  const totalKg = items.reduce((s, x) => s + x.kg_esperados, 0);
                  const prodKg = items.reduce((s, x) => s + Number(x.kg_producidos || 0), 0);
                  const doneCount = items.filter(x => x.terminado).length;
                  const allDone = doneCount === items.length;
                  const groupKey = producto || "—";
                  return (
                    <div key={groupKey} className={`border rounded overflow-hidden ml-4 ${allDone ? "border-emerald-300" : "border-neutral-300"}`}>
                      <div className={`px-4 py-3 flex items-center justify-between gap-3 ${allDone ? "bg-emerald-50" : "bg-neutral-50"}`}>
                        <div className="flex items-center gap-3">
                          <Package size={22} weight="duotone" className={allDone ? "text-emerald-700" : "text-neutral-600"} />
                          <div>
                            <div className="font-bold text-xl text-neutral-800 flex items-center gap-2 flex-wrap">
                              <span>{producto}</span>
                            </div>
                            <div className="text-sm text-neutral-600 font-mono">
                              {items.length} pedido{items.length !== 1 ? "s" : ""} · esperado <strong className="text-neutral-800">{fmtKg(totalKg)}</strong>
                              {prodKg > 0 && <> · producido <strong className="text-emerald-700">{fmtKg(prodKg)}</strong></>}
                            </div>
                          </div>
                        </div>
                        <Badge variant="outline" className={`${allDone ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-white text-neutral-700 border-neutral-300"} text-sm px-3 py-1`}>
                          {doneCount} / {items.length}
                        </Badge>
                      </div>
                      <div className="divide-y divide-neutral-200">
                        {items.map(it => {
                          const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
                          const inputVal = inputKg[key] ?? "";
                          const saving = savingKey === key;
                          const resuelto = it.terminado || it.sin_stock;
                          const rowCls = it.sin_stock
                            ? "bg-red-50/60 opacity-75"
                            : it.terminado
                            ? "bg-emerald-100/80 border-l-4 border-emerald-500"
                            : "bg-white";
                          return (
                            <div
                              key={key}
                              className={`grid grid-cols-12 items-center gap-3 px-4 py-4 ${rowCls}`}
                              data-testid={`ot-row-${key}`}
                            >
                              {/* Cliente + Nº pedido (izquierda) */}
                              <div className="col-span-2 min-w-0">
                                <div className="text-sm text-neutral-800 font-semibold truncate" title={it.cliente_nombre}>
                                  {it.cliente_nombre || "—"}
                                </div>
                                <div className="text-[11px] font-mono text-neutral-500">{it.numero_pedido}</div>
                              </div>

                              {/* Producto (icon + nombre) */}
                              <div className="col-span-2 flex items-center gap-2 min-w-0">
                                {it.sin_stock ? (
                                  <XCircle size={22} weight="fill" className="text-red-600 shrink-0" />
                                ) : it.terminado ? (
                                  <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0" />
                                ) : (
                                  <Clock size={22} weight="regular" className="text-neutral-400 shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <div className={`text-base font-semibold ${resuelto ? "line-through text-neutral-500" : "text-neutral-800"} truncate`}>
                                    {it.producto}
                                  </div>
                                  {it.es_combo && (
                                    <span className="inline-block text-[9px] font-mono font-bold uppercase bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-sm mt-0.5" data-testid={`ot-combo-${key}`}>
                                      COMBO · {it.combo_componentes?.length || 0} prod.
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Especificación (variante) — antes mostraba observaciones del pedido.
                                  Ahora se muestra la variante detectada (Churrascos, Interfoleado, etc.)
                                  Si el producto es un COMBO, en su lugar mostramos el desglose de componentes
                                  con inputs de kg producidos por cada uno. */}
                              <div className="col-span-3 min-w-0">
                                {it.es_combo && (it.combo_componentes || []).length > 0 ? (
                                  <div className="border border-amber-300 bg-amber-50/60 rounded p-2" data-testid={`ot-combo-desglose-${key}`}>
                                    <div className="text-[10px] uppercase text-amber-700 font-mono font-bold leading-tight mb-1">
                                      Componentes ({it.combo_componentes.length})
                                    </div>
                                    <div className="space-y-1">
                                      {it.combo_componentes.map((c, cidx) => {
                                        const kc = `${key}:${cidx}`;
                                        const val = comboInputKg[kc] ?? (c.kg_producidos != null ? String(c.kg_producidos) : "");
                                        return (
                                          <div key={cidx} className="grid grid-cols-[1fr_100px_50px] gap-1 items-center text-[12px]">
                                            <div className="truncate text-neutral-800" title={c.producto}>
                                              ↳ {c.producto}
                                              <span className="text-[10px] font-mono text-neutral-500 ml-1">
                                                esp {Number(c.kg_esperados || 0).toLocaleString("es-AR", { maximumFractionDigits: 3 })}
                                              </span>
                                            </div>
                                            <NumberInput raw
                                              step="0.001"
                                              min="0"
                                              value={val}
                                              placeholder={String(c.kg_esperados || "")}
                                              onChange={(e) => setComboInputKg(prev => ({ ...prev, [kc]: e.target.value }))}
                                              onBlur={() => {
                                                const num = val === "" ? null : Number(val);
                                                if (num !== (c.kg_producidos ?? null)) {
                                                  guardarKgCombo(it, cidx, val === "" ? null : val, c.producto);
                                                }
                                              }}
                                              disabled={resuelto}
                                              className={`w-full text-right font-mono text-[12px] font-bold border rounded px-1 py-1 h-7 ${resuelto ? "bg-neutral-100 text-neutral-500" : "border-amber-400 focus:border-emerald-500"}`}
                                              data-testid={`ot-combo-kg-${kc}`}
                                            />
                                            <span className="text-[10px] text-neutral-500 font-mono">{c.unidad}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : it.especificacion ? (
                                  <div
                                    className="border border-amber-300 bg-amber-50/60 rounded p-2"
                                    title={it.especificacion}
                                    data-testid={`ot-espec-${key}`}
                                  >
                                    <div className="text-[10px] uppercase text-amber-700 font-mono font-bold leading-tight">
                                      Especificación
                                    </div>
                                    <div className="text-[13px] text-neutral-800 whitespace-pre-wrap leading-snug uppercase tracking-wide">
                                      {it.especificacion}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-neutral-300 text-sm">—</span>
                                )}
                              </div>

                              {/* Kg esperados (combos: unidades pedidas + kg calculado por receta) */}
                              <div className="col-span-1 text-right">
                                <div className="text-[10px] uppercase text-neutral-400 font-mono">Esperado</div>
                                {it.es_combo && it.combos_esperados != null ? (
                                  <div>
                                    <div className="text-base font-mono font-bold text-amber-800" data-testid={`ot-combo-esperado-${key}`}>
                                      {it.combos_esperados} combo{it.combos_esperados === 1 ? "" : "s"}
                                    </div>
                                    <div className="text-[10px] font-mono text-neutral-400">{fmtQty(it.kg_esperados, it.unidad)}</div>
                                  </div>
                                ) : (
                                  <div className="text-base font-mono font-bold">{fmtQty(it.kg_esperados, it.unidad)}</div>
                                )}
                              </div>

                              {/* Kg producidos — para combos NO se completa aquí; el operario
                                  carga el kg de cada componente arriba (col. Componentes) y el
                                  sistema lo suma automáticamente al marcar TERMINADO. */}
                              <div className="col-span-1">
                                <div className="text-[10px] uppercase text-neutral-400 font-mono mb-1">Producido</div>
                                {it.es_combo ? (
                                  <div className="space-y-1">
                                    {/* Input de combos completados (unidades) — editable incluso
                                        cuando el ítem ya está terminado (dato informativo) */}
                                    <div className="flex items-center gap-1">
                                      <NumberInput raw
                                        step="1"
                                        min="0"
                                        value={comboUnits[key] ?? (it.combos_completados != null ? String(it.combos_completados) : "")}
                                        placeholder={it.combos_esperados != null ? String(it.combos_esperados) : "0"}
                                        onChange={(e) => setComboUnits(prev => ({ ...prev, [key]: e.target.value }))}
                                        onBlur={(e) => guardarCombosCompletados(it, e.target.value)}
                                        disabled={!!savingCombos[key]}
                                        className={`w-full text-right font-mono text-base font-bold border rounded px-2 py-2 h-11 ${savingCombos[key] ? "bg-neutral-100 text-neutral-500" : "border-amber-300 bg-amber-50/40 focus:border-amber-500"}`}
                                        data-testid={`ot-combos-input-${key}`}
                                      />
                                      <span className="text-xs text-neutral-400 font-mono shrink-0">comb.</span>
                                    </div>
                                    {/* Indicador de faltante de combos */}
                                    {(() => {
                                      if (it.combos_esperados == null) return null;
                                      const raw = comboUnits[key] ?? (it.combos_completados != null ? String(it.combos_completados) : "");
                                      const done = raw === "" ? null : Number(raw);
                                      if (done == null || !Number.isFinite(done)) return null;
                                      const faltan = it.combos_esperados - done;
                                      if (faltan > 0) {
                                        return (
                                          <div className="text-[10px] font-mono font-bold text-amber-700 bg-amber-100 border border-amber-300 rounded-sm px-1.5 py-0.5" data-testid={`ot-combo-faltan-${key}`}>
                                            Faltó preparar {faltan} combo{faltan === 1 ? "" : "s"}
                                          </div>
                                        );
                                      }
                                      if (done === it.combos_esperados) {
                                        return (
                                          <div className="text-[10px] font-mono font-bold text-emerald-700" data-testid={`ot-combo-completo-${key}`}>
                                            Todos los combos listos ✓
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    {/* Σ kg de componentes (referencia) */}
                                    <div className="text-[10px] font-mono text-neutral-400" title="Suma de kg cargados por componente">
                                      Σ {(() => {
                                        const sum = (it.combo_componentes || []).reduce((s, c, cidx) => {
                                          const kc = `${key}:${cidx}`;
                                          const raw = comboInputKg[kc] ?? (c.kg_producidos != null ? String(c.kg_producidos) : "");
                                          const n = Number(raw);
                                          return s + (Number.isFinite(n) ? n : 0);
                                        }, 0);
                                        return sum > 0 ? fmtQty(sum, "kg") : "—";
                                      })()}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <NumberInput raw
                                      step={(it.unidad || "kg").toLowerCase().startsWith("un") ? "1" : "0.001"}
                                      min="0"
                                      value={inputVal}
                                      placeholder={String(it.kg_esperados)}
                                      onChange={(e) => setInputKg(prev => ({ ...prev, [key]: e.target.value }))}
                                      onBlur={() => { if (inputVal !== "" && Number(inputVal) !== (it.kg_producidos ?? -1)) guardarKg(it); }}
                                      disabled={resuelto}
                                      className={`w-full text-right font-mono text-base font-bold border rounded px-2 py-2 h-11 ${resuelto ? "bg-neutral-100 text-neutral-500" : "border-neutral-300 focus:border-emerald-500"}`}
                                      data-testid={`ot-kg-input-${key}`}
                                    />
                                    <span className="text-xs text-neutral-400 font-mono">
                                      {(it.unidad || "kg").toLowerCase().startsWith("un") ? "un" : "kg"}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {/* Botones (3 cols) */}
                              <div className="col-span-3 flex justify-end gap-2">
                                {it.sin_stock ? (
                                  <Button size="sm" variant="outline" className="h-11 text-sm gap-1 border-red-300 text-red-700 hover:bg-red-50"
                                    onClick={() => marcarSinStock(it, false)} disabled={saving}
                                    data-testid={`ot-revertir-sinstock-${key}`}>
                                    <ArrowClockwise size={14} /> Revertir SIN STOCK
                                  </Button>
                                ) : it.terminado ? (
                                  <>
                                    <Button size="sm" variant="outline" className="h-11 text-sm gap-1 border-[#6B1F24]/40 text-[#6B1F24] hover:bg-[#F0D1B6]/40"
                                      onClick={() => {
                                        // Contar cuántos items comparten producto+especificación (= cantidad de pedidos)
                                        const sameGroup = (data.items || []).filter((x) =>
                                          x.producto === it.producto && (x.especificacion || "") === (it.especificacion || "")
                                        );
                                        setPrintItem({ it, defaultCopies: Math.max(1, sameGroup.length) });
                                      }}
                                      data-testid={`ot-imprimir-${key}`}>
                                      <Printer size={14} /> Imprimir etiquetas
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-11 text-sm gap-1 border-neutral-300"
                                      onClick={() => marcarTerminado(it, false)} disabled={saving}
                                      data-testid={`ot-revertir-${key}`}>
                                      <ArrowClockwise size={14} /> Revertir
                                    </Button>
                                  </>
                                ) : (
                                  <>
                                    <Button size="sm" variant="outline"
                                      className="h-11 text-sm gap-1 border-red-300 text-red-700 hover:bg-red-50 font-semibold"
                                      onClick={() => marcarSinStock(it, true)} disabled={saving}
                                      data-testid={`ot-sinstock-${key}`}>
                                      <XCircle size={16} weight="fill" /> SIN STOCK
                                    </Button>
                                    <Button size="sm"
                                      className="h-11 text-sm gap-1 bg-emerald-700 hover:bg-emerald-800 text-white font-semibold"
                                      onClick={() => marcarTerminado(it, true)} disabled={saving}
                                      data-testid={`ot-terminado-${key}`}>
                                      <CheckCircle size={16} weight="fill" /> {saving ? "..." : "TERMINADO"}
                                    </Button>
                                  </>
                                )}
                              </div>
                              {/* Lotes consumidos (si hay) — ocupa toda la fila */}
                              {it.terminado && (it.lotes_consumidos || []).length > 0 && (
                                <div className="col-span-12 flex flex-wrap gap-1 pl-1 pt-1 border-t border-neutral-200/60">
                                  <span className="text-[10px] uppercase font-mono text-neutral-500 self-center">Lotes usados:</span>
                                  {it.lotes_consumidos.map((l, li) => (
                                    <Badge key={li} className="bg-neutral-800 text-white border-0 font-mono text-[10px]">
                                      {l.numero} · {l.kilos} kg
                                    </Badge>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      <SelectorLotesDialog
        open={!!pendingLotes}
        producto={pendingLotes?.it?.producto || ""}
        kilosNecesarios={pendingLotes?.kgP || 0}
        onCancel={() => setPendingLotes(null)}
        onConfirm={async (lotes) => {
          const { it, kgP, combosDone } = pendingLotes || {};
          setPendingLotes(null);
          if (it) await ejecutarMarcarTerminado(it, true, kgP, lotes, combosDone ?? null);
        }}
      />

      <ImprimirEtiquetasOTDialog
        printItem={printItem}
        zebraConfig={zebraOutputConfigRef.current}
        onClose={() => setPrintItem(null)}
      />

      {/* Dialog "Terminados del día" — mismo layout que En Facturación */}
      <Dialog open={terminadosDialog} onOpenChange={setTerminadosDialog}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              Terminados del día · {fecha} · {totalTerminadosCombinado} pedido{totalTerminadosCombinado !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              Todos los productos que ya terminaron producción — en la Orden de Trabajo, listos para facturar o ya facturados.
            </DialogDescription>
          </DialogHeader>

          {gruposTerminados.length === 0 ? (
            <div className="border-2 border-dashed border-neutral-300 rounded p-12 text-center">
              <WarningCircle size={40} className="mx-auto text-neutral-300 mb-3" />
              <div className="text-neutral-500">Aún no hay pedidos terminados en <strong>{fecha}</strong>.</div>
            </div>
          ) : (
            <div className="space-y-6">
              {gruposTerminados.map((rubro) => {
                const productosArr = Array.from(rubro.productos.values());
                const rubroItems = productosArr.flatMap((g) => g.items);
                const totalKgRubro = rubroItems.reduce((s, x) => s + x.kg_esperados, 0);
                const prodKgRubro = rubroItems.reduce((s, x) => s + Number(x.kg_producidos || 0), 0);
                return (
                  <div key={rubro.nombre} className="space-y-2" data-testid={`terminados-rubro-${rubro.nombre}`}>
                    <div className="rounded flex items-center gap-3 px-4 py-3 text-white font-bold shadow-sm" style={{ background: rubro.color }}>
                      <div className="w-3 h-3 rounded-full bg-white/80" style={{ boxShadow: `0 0 0 3px ${rubro.color}` }} />
                      <div className="flex-1">
                        <div className="text-lg">{rubro.nombre}</div>
                        <div className="text-xs opacity-90 font-mono">
                          {rubroItems.length} ítems · {fmtKg(totalKgRubro)} esperado · <span className="text-white">{fmtKg(prodKgRubro)} producido</span>
                        </div>
                      </div>
                    </div>
                    {productosArr.map((grupo) => {
                      const { producto, items } = grupo;
                      const totalKg = items.reduce((s, x) => s + x.kg_esperados, 0);
                      const prodKg = items.reduce((s, x) => s + Number(x.kg_producidos || 0), 0);
                      const groupKey = producto || "—";
                      return (
                        <div key={groupKey} className="border rounded overflow-hidden ml-4 border-emerald-300">
                          <div className="px-4 py-3 flex items-center justify-between gap-3 bg-emerald-50">
                            <div className="flex items-center gap-3">
                              <Package size={22} weight="duotone" className="text-emerald-700" />
                              <div>
                                <div className="font-bold text-lg text-neutral-800">{producto}</div>
                                <div className="text-sm text-neutral-600 font-mono">
                                  {items.length} pedido{items.length !== 1 ? "s" : ""} · esperado <strong className="text-neutral-800">{fmtKg(totalKg)}</strong> · producido <strong className="text-emerald-700">{fmtKg(prodKg)}</strong>
                                </div>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="gap-1 border-emerald-500 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800"
                              onClick={() => {
                                setPrintItem({
                                  it: { ...items[0], kg_producidos: prodKg },
                                  defaultCopies: Math.max(1, items.length),
                                });
                              }}
                              data-testid={`terminados-imprimir-${groupKey}`}
                            >
                              <Printer size={14} weight="bold" /> Reimprimir etiquetas
                            </Button>
                          </div>
                          <div className="divide-y divide-neutral-200">
                            {items.map((it) => {
                              const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
                              const meta = enFacturacion.pedidos_meta?.[`${it.source_type}:${it.source_id}`] || {};
                              // Un item de OT (aún en_produccion) NO está listo para facturar; sólo los de en-facturacion pueden tener meta.
                              const esListoParaFacturar = enFacturacion.items?.some(
                                (x) => x.source_type === it.source_type && x.source_id === it.source_id && x.item_idx === it.item_idx
                              );
                              return (
                                <div key={key} className="grid grid-cols-12 items-center gap-3 px-4 py-3 bg-white">
                                  <div className="col-span-2 min-w-0">
                                    <div className="text-sm text-neutral-800 font-semibold truncate" title={it.cliente_nombre}>
                                      {it.cliente_nombre || "—"}
                                    </div>
                                    <div className="text-[11px] font-mono text-neutral-500">{it.numero_pedido}</div>
                                  </div>
                                  <div className="col-span-2 flex items-center gap-2 min-w-0">
                                    {it.sin_stock ? (
                                      <XCircle size={20} weight="fill" className="text-red-600 shrink-0" />
                                    ) : (
                                      <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />
                                    )}
                                    <div className="text-base font-semibold text-neutral-700 truncate">
                                      {it.producto}
                                    </div>
                                  </div>
                                  <div className="col-span-3 min-w-0">
                                    {it.especificacion ? (
                                      <div className="border border-amber-300 bg-amber-50/60 rounded p-2">
                                        <div className="text-[10px] uppercase text-amber-700 font-mono font-bold leading-tight">Especificación</div>
                                        <div className="text-[13px] text-neutral-800 whitespace-pre-wrap leading-snug uppercase tracking-wide">{it.especificacion}</div>
                                      </div>
                                    ) : (
                                      <span className="text-neutral-300 text-sm">—</span>
                                    )}
                                  </div>
                                  <div className="col-span-1 text-right">
                                    <div className="text-[10px] uppercase text-neutral-400 font-mono">Esperado</div>
                                    <div className="text-base font-mono font-bold">{fmtQty(it.kg_esperados, it.unidad)}</div>
                                  </div>
                                  <div className="col-span-1 text-right">
                                    <div className="text-[10px] uppercase text-neutral-400 font-mono">Producido</div>
                                    <div className="text-base font-mono font-bold text-emerald-700">
                                      {it.kg_producidos != null ? fmtQty(it.kg_producidos, it.unidad) : "—"}
                                    </div>
                                  </div>
                                  <div className="col-span-3 flex justify-end">
                                    {meta.facturado ? (
                                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Facturado</Badge>
                                    ) : esListoParaFacturar ? (
                                      <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50">Pendiente de facturar</Badge>
                                    ) : (
                                      <Badge variant="outline" className="border-neutral-400 text-neutral-700 bg-neutral-50">En producción</Badge>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog "En Facturación" — mismo layout que la OT pero readonly */}
      <Dialog open={enFacDialog} onOpenChange={setEnFacDialog}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRight size={18} className="text-amber-600" />
              En Facturación · {fecha} · {enFacturacion.total_pedidos} pedido{enFacturacion.total_pedidos !== 1 ? "s" : ""} listo{enFacturacion.total_pedidos !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              Pedidos que ya terminaron producción. Al facturarlos, salen de esta lista.
            </DialogDescription>
          </DialogHeader>

          {gruposEnFacturacion.length === 0 ? (
            <div className="border-2 border-dashed border-neutral-300 rounded p-12 text-center">
              <WarningCircle size={40} className="mx-auto text-neutral-300 mb-3" />
              <div className="text-neutral-500">Aún no hay pedidos listos para facturar en <strong>{fecha}</strong>.</div>
            </div>
          ) : (
            <div className="space-y-6">
              {gruposEnFacturacion.map((rubro) => {
                const productosArr = Array.from(rubro.productos.values());
                const rubroItems = productosArr.flatMap((g) => g.items);
                const totalKgRubro = rubroItems.reduce((s, x) => s + x.kg_esperados, 0);
                const prodKgRubro = rubroItems.reduce((s, x) => s + Number(x.kg_producidos || 0), 0);
                return (
                  <div key={rubro.nombre} className="space-y-2" data-testid={`enfac-rubro-${rubro.nombre}`}>
                    <div className="rounded flex items-center gap-3 px-4 py-3 text-white font-bold shadow-sm" style={{ background: rubro.color }}>
                      <div className="w-3 h-3 rounded-full bg-white/80" style={{ boxShadow: `0 0 0 3px ${rubro.color}` }} />
                      <div className="flex-1">
                        <div className="text-lg">{rubro.nombre}</div>
                        <div className="text-xs opacity-90 font-mono">
                          {rubroItems.length} ítems · {fmtKg(totalKgRubro)} esperado · <span className="text-white">{fmtKg(prodKgRubro)} producido</span>
                        </div>
                      </div>
                    </div>
                    {productosArr.map((grupo) => {
                      const { producto, items } = grupo;
                      const totalKg = items.reduce((s, x) => s + x.kg_esperados, 0);
                      const prodKg = items.reduce((s, x) => s + Number(x.kg_producidos || 0), 0);
                      const groupKey = producto || "—";
                      return (
                        <div key={groupKey} className="border rounded overflow-hidden ml-4 border-emerald-300">
                          <div className="px-4 py-3 flex items-center justify-between gap-3 bg-emerald-50">
                            <div className="flex items-center gap-3">
                              <Package size={22} weight="duotone" className="text-emerald-700" />
                              <div>
                                <div className="font-bold text-lg text-neutral-800">{producto}</div>
                                <div className="text-sm text-neutral-600 font-mono">
                                  {items.length} pedido{items.length !== 1 ? "s" : ""} · esperado <strong className="text-neutral-800">{fmtKg(totalKg)}</strong> · producido <strong className="text-emerald-700">{fmtKg(prodKg)}</strong>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="divide-y divide-neutral-200">
                            {items.map((it) => {
                              const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
                              const meta = enFacturacion.pedidos_meta?.[`${it.source_type}:${it.source_id}`] || {};
                              return (
                                <div key={key} className="grid grid-cols-12 items-center gap-3 px-4 py-3 bg-white">
                                  <div className="col-span-2 min-w-0">
                                    <div className="text-sm text-neutral-800 font-semibold truncate" title={it.cliente_nombre}>
                                      {it.cliente_nombre || "—"}
                                    </div>
                                    <div className="text-[11px] font-mono text-neutral-500">{it.numero_pedido}</div>
                                  </div>
                                  <div className="col-span-2 flex items-center gap-2 min-w-0">
                                    {it.sin_stock ? (
                                      <XCircle size={20} weight="fill" className="text-red-600 shrink-0" />
                                    ) : (
                                      <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />
                                    )}
                                    <div className="text-base font-semibold text-neutral-700 truncate">
                                      {it.producto}
                                    </div>
                                  </div>
                                  <div className="col-span-3 min-w-0">
                                    {it.especificacion ? (
                                      <div className="border border-amber-300 bg-amber-50/60 rounded p-2">
                                        <div className="text-[10px] uppercase text-amber-700 font-mono font-bold leading-tight">Especificación</div>
                                        <div className="text-[13px] text-neutral-800 whitespace-pre-wrap leading-snug uppercase tracking-wide">{it.especificacion}</div>
                                      </div>
                                    ) : (
                                      <span className="text-neutral-300 text-sm">—</span>
                                    )}
                                  </div>
                                  <div className="col-span-1 text-right">
                                    <div className="text-[10px] uppercase text-neutral-400 font-mono">Esperado</div>
                                    <div className="text-base font-mono font-bold">{fmtQty(it.kg_esperados, it.unidad)}</div>
                                  </div>
                                  <div className="col-span-1 text-right">
                                    <div className="text-[10px] uppercase text-neutral-400 font-mono">Producido</div>
                                    <div className="text-base font-mono font-bold text-emerald-700">
                                      {it.kg_producidos != null ? fmtQty(it.kg_producidos, it.unidad) : "—"}
                                    </div>
                                  </div>
                                  <div className="col-span-3 flex justify-end">
                                    {meta.facturado ? (
                                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">Facturado</Badge>
                                    ) : (
                                      <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50">Pendiente de facturar</Badge>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}

          <div className="text-[11px] text-neutral-500 font-mono">
            Facturá desde <strong>Ventas → Facturación</strong> o desde las pestañas de Pedidos / Tienda Nube.
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog "Facturados" — mismo layout que Terminados pero readonly, con número de factura */}
      <Dialog open={facturadosDialog} onOpenChange={setFacturadosDialog}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt size={20} weight="duotone" className="text-blue-600" />
              Facturados · {fecha} · {facturados.total_pedidos} pedido{facturados.total_pedidos !== 1 ? "s" : ""}
            </DialogTitle>
            <DialogDescription>
              Pedidos ya facturados del día. Podés revisar los ítems, kilos producidos y el número de factura.
            </DialogDescription>
          </DialogHeader>

          {gruposFacturados.length === 0 ? (
            <div className="border-2 border-dashed border-neutral-300 rounded p-12 text-center">
              <WarningCircle size={40} className="mx-auto text-neutral-300 mb-3" />
              <div className="text-neutral-500">Aún no hay pedidos facturados en <strong>{fecha}</strong>.</div>
            </div>
          ) : (
            <div className="space-y-6">
              {gruposFacturados.map((rubro) => {
                const productosArr = Array.from(rubro.productos.values());
                const rubroItems = productosArr.flatMap((g) => g.items);
                const totalKgRubro = rubroItems.reduce((s, x) => s + x.kg_esperados, 0);
                const prodKgRubro = rubroItems.reduce((s, x) => s + Number(x.kg_producidos || 0), 0);
                return (
                  <div key={rubro.nombre} className="space-y-2" data-testid={`facturados-rubro-${rubro.nombre}`}>
                    <div className="rounded flex items-center gap-3 px-4 py-3 text-white font-bold shadow-sm" style={{ background: rubro.color }}>
                      <div className="w-3 h-3 rounded-full bg-white/80" style={{ boxShadow: `0 0 0 3px ${rubro.color}` }} />
                      <div className="flex-1">
                        <div className="text-lg">{rubro.nombre}</div>
                        <div className="text-xs opacity-90 font-mono">
                          {rubroItems.length} ítems · {fmtKg(totalKgRubro)} esperado · <span className="text-white">{fmtKg(prodKgRubro)} producido</span>
                        </div>
                      </div>
                    </div>
                    {productosArr.map((grupo) => {
                      const { producto, items } = grupo;
                      const totalKg = items.reduce((s, x) => s + x.kg_esperados, 0);
                      const prodKg = items.reduce((s, x) => s + Number(x.kg_producidos || 0), 0);
                      const groupKey = producto || "—";
                      return (
                        <div key={groupKey} className="border rounded overflow-hidden ml-4 border-blue-300">
                          <div className="px-4 py-3 flex items-center justify-between gap-3 bg-blue-50">
                            <div className="flex items-center gap-3">
                              <Package size={22} weight="duotone" className="text-blue-700" />
                              <div>
                                <div className="font-bold text-lg text-neutral-800">{producto}</div>
                                <div className="text-sm text-neutral-600 font-mono">
                                  {items.length} pedido{items.length !== 1 ? "s" : ""} · esperado <strong className="text-neutral-800">{fmtKg(totalKg)}</strong> · producido <strong className="text-blue-700">{fmtKg(prodKg)}</strong>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="divide-y divide-neutral-200">
                            {items.map((it) => {
                              const key = `${it.source_type}:${it.source_id}:${it.item_idx}`;
                              const meta = facturados.pedidos_meta?.[`${it.source_type}:${it.source_id}`] || {};
                              return (
                                <div key={key} className="grid grid-cols-12 items-center gap-3 px-4 py-3 bg-white">
                                  <div className="col-span-2 min-w-0">
                                    <div className="text-sm text-neutral-800 font-semibold truncate" title={it.cliente_nombre}>
                                      {it.cliente_nombre || "—"}
                                    </div>
                                    <div className="text-[11px] font-mono text-neutral-500">{it.numero_pedido}</div>
                                  </div>
                                  <div className="col-span-2 flex items-center gap-2 min-w-0">
                                    {it.sin_stock ? (
                                      <XCircle size={20} weight="fill" className="text-red-600 shrink-0" />
                                    ) : (
                                      <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0" />
                                    )}
                                    <div className="text-base font-semibold text-neutral-700 truncate">
                                      {it.producto}
                                    </div>
                                  </div>
                                  <div className="col-span-3 min-w-0">
                                    {it.especificacion ? (
                                      <div className="border border-amber-300 bg-amber-50/60 rounded p-2">
                                        <div className="text-[10px] uppercase text-amber-700 font-mono font-bold leading-tight">Especificación</div>
                                        <div className="text-[13px] text-neutral-800 whitespace-pre-wrap leading-snug uppercase tracking-wide">{it.especificacion}</div>
                                      </div>
                                    ) : (
                                      <span className="text-neutral-300 text-sm">—</span>
                                    )}
                                  </div>
                                  <div className="col-span-1 text-right">
                                    <div className="text-[10px] uppercase text-neutral-400 font-mono">Esperado</div>
                                    <div className="text-base font-mono font-bold">{fmtQty(it.kg_esperados, it.unidad)}</div>
                                  </div>
                                  <div className="col-span-1 text-right">
                                    <div className="text-[10px] uppercase text-neutral-400 font-mono">Producido</div>
                                    <div className="text-base font-mono font-bold text-emerald-700">
                                      {it.kg_producidos != null ? fmtQty(it.kg_producidos, it.unidad) : "—"}
                                    </div>
                                  </div>
                                  <div className="col-span-3 flex justify-end">
                                    <Badge className="bg-blue-100 text-blue-800 border-blue-300 font-mono" data-testid={`facturados-factura-${key}`}>
                                      <Receipt size={12} weight="bold" className="mr-1" />
                                      {meta.factura_numero ? `Fact. #${meta.factura_numero}` : "Facturado"}
                                    </Badge>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertaExcesoKgTNModal
        data={excesoKgAlerta}
        onClose={() => setExcesoKgAlerta(null)}
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, colorClass = "text-neutral-800" }) {
  return (
    <div className="border border-neutral-300 rounded bg-white px-4 py-3 flex items-center gap-3">
      <Icon size={22} weight="regular" className={colorClass} />
      <div>
        <div className="text-[10px] uppercase font-mono text-neutral-500 tracking-widest">{label}</div>
        <div className={`text-xl font-bold ${colorClass}`}>{value}</div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════
// Dialog para imprimir etiquetas Zebra al terminar un producto en OT
// ═════════════════════════════════════════════════════════════════════
function ImprimirEtiquetasOTDialog({ printItem, zebraConfig, onClose }) {
  const printer = useZebraPrinter();
  const [copies, setCopies] = useState(1);
  const [kgPorEtiqueta, setKgPorEtiqueta] = useState("");
  const [personalizar, setPersonalizar] = useState(false);
  const [pesos, setPesos] = useState([]); // array de strings/numbers, uno por etiqueta
  const [printing, setPrinting] = useState(false);
  // Config de impresión por componente (sólo combos):
  // [{ copies, kgPorEtiqueta, personalizar, pesos[] }]
  const [comboCfg, setComboCfg] = useState([]);

  // Inicialización al abrir el diálogo con un item nuevo
  useEffect(() => {
    if (!printItem) return;
    const defCopies = printItem.defaultCopies || 1;
    const kg = Number(printItem.it.kg_producidos || printItem.it.kg_esperados || 0);
    setCopies(defCopies);
    setKgPorEtiqueta((kg / defCopies).toFixed(3));
    setPesos(Array.from({ length: defCopies }, () => (kg / defCopies).toFixed(3)));
    setPersonalizar(false);
    // Combos: 1 etiqueta por componente con el kg que se cargó en la OT
    // (o el esperado si todavía no se cargó). Los kg se editan acá y NO se
    // persisten: sólo afectan a las etiquetas impresas.
    if (printItem.it.es_combo && Array.isArray(printItem.it.combo_componentes)) {
      setComboCfg(printItem.it.combo_componentes.map((c) => {
        const kgc = Number(c.kg_producidos ?? c.kg_esperados ?? 0);
        return { copies: 1, kgPorEtiqueta: kgc.toFixed(3), personalizar: false, pesos: [kgc.toFixed(3)] };
      }));
    } else {
      setComboCfg([]);
    }
  }, [printItem]);

  // Cuando cambia la cantidad de etiquetas: re-distribuir uniforme
  useEffect(() => {
    if (!printItem) return;
    const kg = Number(printItem.it.kg_producidos || printItem.it.kg_esperados || 0);
    const c = Math.max(1, parseInt(copies) || 1);
    const kgU = kg / c;
    setKgPorEtiqueta(kgU.toFixed(3));
    setPesos(Array.from({ length: c }, () => kgU.toFixed(3)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copies]);

  if (!printItem) return null;
  const { it } = printItem;

  const esCombo = !!(it.es_combo && Array.isArray(it.combo_componentes) && it.combo_componentes.length > 0);
  const comboComponentes = esCombo ? it.combo_componentes : [];

  const hoy = new Date();
  const venc = new Date();
  venc.setDate(venc.getDate() + 15);
  const fmtDate = (d) => d.toISOString().slice(0, 10);
  const fechaElab = fmtDate(hoy);
  const fechaVenc = fmtDate(venc);
  const kgNetos = esCombo
    ? comboComponentes.reduce((s, c) => s + Number(c.kg_producidos ?? c.kg_esperados ?? 0), 0)
    : Number(it.kg_producidos || it.kg_esperados || 0);

  // Helpers de configuración por componente (combos)
  const kgCompTotal = (cidx) =>
    Number(comboComponentes[cidx]?.kg_producidos ?? comboComponentes[cidx]?.kg_esperados ?? 0);
  const setComp = (cidx, patch) =>
    setComboCfg((cur) => cur.map((cfg, i) => (i === cidx ? { ...cfg, ...patch } : cfg)));
  const setCompCopies = (cidx, n) => {
    const c = Math.max(1, parseInt(n) || 1);
    const kgU = (kgCompTotal(cidx) / c).toFixed(3);
    setComp(cidx, { copies: c, kgPorEtiqueta: kgU, pesos: Array.from({ length: c }, () => kgU) });
  };
  const totalEtiquetasCombo = comboCfg.reduce(
    (s, cfg) => s + (cfg.personalizar ? cfg.pesos.length : Math.max(1, parseInt(cfg.copies) || 1)),
    0
  );

  // Suma de kg configurados (items no-combo)
  const kgConfigTotal = personalizar
    ? pesos.reduce((s, p) => s + (parseFloat(p) || 0), 0)
    : (parseFloat(kgPorEtiqueta) || 0) * (parseInt(copies) || 0);
  const diffKg = kgNetos - kgConfigTotal;

  const handlePrint = async () => {
    if (!printer.device) {
      try { await printer.connect(); } catch (_) { /* ignore */ }
    }
    setPrinting(true);
    try {
      let zpl;
      let n;
      if (esCombo) {
        // Una o más etiquetas POR COMPONENTE: nombre = combo, variante =
        // componente. Los kg editados acá no se guardan en la OT.
        const parts = [];
        n = 0;
        comboCfg.forEach((cfg, cidx) => {
          const comp = comboComponentes[cidx] || {};
          const base = {
            nombre: it.producto || "",
            variante: comp.producto || "",
            componente: comp.producto || "",
            fechaElab,
            fechaVenc,
            lote: "",
            qrUrl: "",
            frigorifico: "LA BIFERIA S.R.L.",
            cuitFrigorifico: "",
            config: zebraConfig,
          };
          if (cfg.personalizar) {
            cfg.pesos.forEach((peso) => {
              parts.push(generarZplEtiqueta({ ...base, peso: parseFloat(peso) || 0, copies: 1 }));
              n += 1;
            });
          } else {
            const c = Math.max(1, parseInt(cfg.copies) || 1);
            parts.push(generarZplEtiqueta({ ...base, peso: parseFloat(cfg.kgPorEtiqueta) || 0, copies: c }));
            n += c;
          }
        });
        zpl = parts.join("\n");
      } else if (personalizar) {
        // Un ZPL por cada etiqueta con su peso propio (copies=1 c/u).
        zpl = pesos.map((peso) => generarZplEtiqueta({
          nombre: it.producto || "",
          variante: it.especificacion || "",
          peso: parseFloat(peso) || 0,
          fechaElab,
          fechaVenc,
          lote: "",
          qrUrl: "",
          frigorifico: "LA BIFERIA S.R.L.",
          cuitFrigorifico: "",
          copies: 1,
          config: zebraConfig,
        })).join("\n");
        n = pesos.length;
      } else {
        // Modo uniforme: N copias con el mismo peso
        zpl = generarZplEtiqueta({
          nombre: it.producto || "",
          variante: it.especificacion || "",
          peso: parseFloat(kgPorEtiqueta) || 0,
          fechaElab,
          fechaVenc,
          lote: "",
          qrUrl: "",
          frigorifico: "LA BIFERIA S.R.L.",
          cuitFrigorifico: "",
          copies: Math.max(1, parseInt(copies) || 1),
          config: zebraConfig,
        });
        n = Math.max(1, parseInt(copies) || 1);
      }
      await printer.print(zpl);
      toast.success(`${n} etiqueta${n === 1 ? "" : "s"} enviada${n === 1 ? "" : "s"} a la Zebra`);
      onClose();
    } catch (e) {
      toast.error(e?.message || "No se pudo imprimir en la Zebra");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Dialog open={!!printItem} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#6B1F24]">
            <Printer size={20} weight="duotone" /> Imprimir etiquetas · {it.producto}
          </DialogTitle>
          <DialogDescription>
            Se imprimirá en la Zebra con la plantilla "Salida de Producto".
          </DialogDescription>
        </DialogHeader>

        <div className="border border-neutral-200 rounded p-3 bg-neutral-50 space-y-1 text-[13px]">
          <div className="flex justify-between">
            <span className="text-neutral-500">Producto:</span>
            <span className="font-semibold uppercase">{it.producto}</span>
          </div>
          {it.especificacion && (
            <div className="flex justify-between">
              <span className="text-neutral-500">Especificación:</span>
              <span className="font-semibold">{it.especificacion}</span>
            </div>
          )}
          {esCombo && (
            <div className="flex justify-between">
              <span className="text-neutral-500">Combo:</span>
              <span className="font-semibold text-amber-800">{comboComponentes.length} productos</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-neutral-500">Kg totales producidos:</span>
            <span className="font-mono font-bold text-[#6B1F24]">{kgNetos.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-500">Elaboración:</span>
            <span className="font-mono">{fechaElab}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-neutral-500">Vencimiento (elab + 15 días):</span>
            <span className="font-mono">{fechaVenc}</span>
          </div>
        </div>

        {esCombo ? (
          <div className="space-y-3" data-testid="ot-imprimir-combo-componentes">
            {comboComponentes.map((comp, cidx) => {
              const cfg = comboCfg[cidx] || { copies: 1, kgPorEtiqueta: "0.000", personalizar: false, pesos: [] };
              const kgTotComp = kgCompTotal(cidx);
              const kgConfigComp = cfg.personalizar
                ? cfg.pesos.reduce((s, p) => s + (parseFloat(p) || 0), 0)
                : (parseFloat(cfg.kgPorEtiqueta) || 0) * (parseInt(cfg.copies) || 0);
              const diffComp = kgTotComp - kgConfigComp;
              return (
                <div key={cidx} className="border border-amber-300 bg-amber-50/50 rounded p-3 space-y-2"
                  data-testid={`ot-imprimir-combo-comp-${cidx}`}>
                  <div className="flex justify-between items-baseline gap-2">
                    <span className="font-semibold uppercase text-[13px]">{comp.producto}</span>
                    <span className="font-mono text-[12px] text-neutral-600 shrink-0">{kgTotComp.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px] uppercase tracking-widest font-mono text-neutral-500">
                        Etiquetas
                      </Label>
                      <NumberInput
                        min={1}
                        max={200}
                        value={cfg.copies}
                        onChange={(e) => setCompCopies(cidx, e.target.value)}
                        disabled={cfg.personalizar}
                        className="mt-1 h-10 font-mono text-center"
                        data-testid={`ot-imprimir-combo-copies-${cidx}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-widest font-mono text-neutral-500">
                        Kg por etiqueta
                      </Label>
                      <NumberInput
                        min={0}
                        step={0.001}
                        value={cfg.kgPorEtiqueta}
                        onChange={(e) => setComp(cidx, { kgPorEtiqueta: e.target.value })}
                        disabled={cfg.personalizar}
                        className="mt-1 h-10 font-mono text-center"
                        data-testid={`ot-imprimir-combo-kg-${cidx}`}
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 py-1.5 px-2 border border-neutral-200 rounded cursor-pointer hover:bg-white text-[12px]">
                    <input
                      type="checkbox"
                      checked={cfg.personalizar}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const nC = Math.max(1, parseInt(cfg.copies) || 1);
                          const kgU = cfg.kgPorEtiqueta || (kgTotComp / nC).toFixed(3);
                          setComp(cidx, { personalizar: true, pesos: Array.from({ length: nC }, () => kgU) });
                        } else {
                          setComp(cidx, { personalizar: false });
                        }
                      }}
                      data-testid={`ot-imprimir-combo-personalizar-${cidx}`}
                    />
                    <span>Separar en bandejas (peso individual por etiqueta)</span>
                  </label>
                  {cfg.personalizar && (
                    <div className="border border-neutral-300 rounded p-2 bg-white space-y-2">
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-40 overflow-y-auto pr-1">
                        {cfg.pesos.map((p, i) => (
                          <div key={i} className="flex items-center gap-1">
                            <span className="text-[10px] font-mono text-neutral-400 w-5 text-right">#{i + 1}</span>
                            <NumberInput
                              min={0}
                              step={0.001}
                              value={p}
                              onChange={(e) => {
                                const next = [...cfg.pesos];
                                next[i] = e.target.value;
                                setComp(cidx, { pesos: next });
                              }}
                              className="h-8 font-mono text-[12px] text-right"
                              data-testid={`ot-imprimir-combo-peso-${cidx}-${i}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 pt-1 border-t border-neutral-200">
                        <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          onClick={() => {
                            const kgU = (kgTotComp / (cfg.pesos.length || 1)).toFixed(3);
                            setComp(cidx, { pesos: cfg.pesos.map(() => kgU) });
                          }}>
                          Distribuir uniforme
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          onClick={() => setComp(cidx, { pesos: [...cfg.pesos, "0.000"], copies: cfg.pesos.length + 1 })}>
                          + Agregar
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px]"
                          disabled={cfg.pesos.length <= 1}
                          onClick={() => setComp(cidx, { pesos: cfg.pesos.slice(0, -1), copies: Math.max(1, cfg.pesos.length - 1) })}>
                          − Quitar
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className={`text-[11px] font-mono rounded p-1.5 ${Math.abs(diffComp) < 0.01 ? "bg-emerald-50 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
                    data-testid={`ot-imprimir-combo-total-${cidx}`}>
                    Total: <strong>{kgConfigComp.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg</strong>
                    {Math.abs(diffComp) >= 0.01 && (
                      <> · Dif: <strong>{diffComp > 0 ? "+" : ""}{diffComp.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg</strong> vs. producido</>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="text-[11px] text-neutral-500">
              Los kg editados acá sólo se usan para imprimir: no modifican la OT.
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-neutral-500">
                  Cantidad de etiquetas
                </Label>
                <NumberInput
                  min={1}
                  max={200}
                  value={copies}
                  onChange={(e) => setCopies(Math.max(1, parseInt(e.target.value) || 1))}
                  disabled={personalizar}
                  className="mt-1 h-11 font-mono text-lg text-center"
                  data-testid="ot-imprimir-copies"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-neutral-500">
                  Kg por etiqueta
                </Label>
                <NumberInput
                  min={0}
                  step={0.001}
                  value={kgPorEtiqueta}
                  onChange={(e) => setKgPorEtiqueta(e.target.value)}
                  disabled={personalizar}
                  className="mt-1 h-11 font-mono text-lg text-center"
                  data-testid="ot-imprimir-kg"
                />
              </div>
            </div>
            <div className="text-[11px] text-neutral-500 -mt-1">
              Predeterminado: {printItem.defaultCopies} etiqueta{printItem.defaultCopies !== 1 ? "s" : ""} de {(kgNetos / printItem.defaultCopies).toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg c/u.
            </div>

            <label className="flex items-center gap-2 py-2 px-3 border border-neutral-200 rounded cursor-pointer hover:bg-neutral-50 text-[13px]">
              <input
                type="checkbox"
                checked={personalizar}
                onChange={(e) => setPersonalizar(e.target.checked)}
                data-testid="ot-imprimir-personalizar"
              />
              <span>Personalizar peso individual por etiqueta</span>
            </label>

            {personalizar && (
              <div className="border border-neutral-300 rounded p-2 bg-neutral-50 space-y-2">
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-56 overflow-y-auto pr-1">
                  {pesos.map((p, i) => (
                    <div key={i} className="flex items-center gap-1">
                      <span className="text-[10px] font-mono text-neutral-400 w-5 text-right">#{i + 1}</span>
                      <NumberInput
                        min={0}
                        step={0.001}
                        value={p}
                        onChange={(e) => {
                          const next = [...pesos];
                          next[i] = e.target.value;
                          setPesos(next);
                        }}
                        className="h-8 font-mono text-[12px] text-right"
                        data-testid={`ot-imprimir-peso-${i}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2 pt-1 border-t border-neutral-200">
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => {
                      const kgU = (kgNetos / (pesos.length || 1)).toFixed(3);
                      setPesos(pesos.map(() => kgU));
                    }}>
                    Distribuir uniforme
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    onClick={() => {
                      setPesos((prev) => [...prev, "0.000"]);
                      setCopies((c) => c + 1);
                    }}>
                    + Agregar
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]"
                    disabled={pesos.length <= 1}
                    onClick={() => {
                      setPesos((prev) => prev.slice(0, -1));
                      setCopies((c) => Math.max(1, c - 1));
                    }}>
                    − Quitar
                  </Button>
                </div>
              </div>
            )}

            <div className={`text-[11px] font-mono rounded p-2 ${Math.abs(diffKg) < 0.01 ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>
              Total configurado: <strong>{kgConfigTotal.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg</strong>
              {Math.abs(diffKg) >= 0.01 && (
                <> · Diferencia: <strong>{diffKg > 0 ? "+" : ""}{diffKg.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg</strong> vs. producido</>
              )}
            </div>
          </>
        )}

        <div className="border border-neutral-200 rounded-md bg-neutral-50 p-2 flex items-center justify-between text-[12px]">
          <div>
            <span className="font-mono uppercase text-neutral-500 text-[10px]">Impresora Zebra: </span>
            {printer.device ? (
              <span className="font-medium text-emerald-700">✓ {printer.device.productName || "Conectada"}</span>
            ) : (
              <span className="text-neutral-500">no conectada</span>
            )}
          </div>
          {!printer.device && (
            <Button size="sm" onClick={printer.connect} disabled={printer.connecting || !printer.supported}
              className="gap-1 h-8 text-[11px]" data-testid="ot-zebra-conectar">
              <Printer size={12} /> {printer.connecting ? "Conectando…" : "Conectar Zebra"}
            </Button>
          )}
        </div>

        {!printer.supported && (
          <div className="text-[11px] bg-amber-50 border border-amber-200 rounded-md p-2 text-amber-800">
            Este navegador no soporta WebUSB. Usá Chrome, Edge u Opera para imprimir directo.
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={printing}>Cerrar</Button>
          <Button onClick={handlePrint} disabled={printing || !printer.supported}
            className="bg-[#6B1F24] hover:bg-[#571519] gap-1"
            data-testid="ot-imprimir-confirmar">
            <Printer size={14} /> {printing ? "Imprimiendo…" : `Imprimir ${esCombo ? totalEtiquetasCombo : (personalizar ? pesos.length : copies)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
